// src/components/schoolmate/SchoolmateSettings.jsx
import React, { useState, useEffect } from 'react';
import { School, Calendar, Users, BookOpen, Settings, Palette, Globe, Shield, Layout, Bell, Clock } from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { SEOSection } from '../settings/shared/SEOSection';
import { firestoreService } from '../../services/firestoreService';

const GRADE_LEVELS = ['R', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const SCHOOL_TYPES = [
  { value: 'primary', label: 'Primary School' },
  { value: 'secondary', label: 'Secondary School' },
  { value: 'combined', label: 'Combined School' },
  { value: 'special', label: 'Special Needs' },
  { value: 'college', label: 'College/TVET' }
];
const TERM_STRUCTURES = [
  { value: 'quarterly', label: '4 Terms' },
  { value: 'semester', label: '2 Semesters' },
  { value: 'trimester', label: '3 Trimesters' }
];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const FEATURES = {
  timetable: { label: 'Timetable', icon: Calendar, desc: 'Manage schedules' },
  lessons: { label: 'Lesson Tracking', icon: BookOpen, desc: 'Track lesson delivery' },
  attendance: { label: 'Attendance', icon: Users, desc: 'Record attendance' },
  assessments: { label: 'Assessments', icon: Layout, desc: 'Manage tests & marks' },
  operationalPlans: { label: 'Operational Plans', icon: Settings, desc: 'Strategic planning' },
  reports: { label: 'Reports', icon: Globe, desc: 'Analytics & reports' }
};

export const SchoolmateSettings = ({ book, bookId, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadStates, setUploadStates] = useState({});
  const [errors, setErrors] = useState({});

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Settings },
    { id: 'school', label: 'School Setup', icon: School },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    { id: 'features', label: 'Features', icon: Layout },
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'seo', label: 'SEO', icon: Globe }
  ];

  useEffect(() => {
    if (book) {
      setFormData({
        botName: book.botName || '',
        description: book.description || '',
        logoUrl: book.logoUrl || '',
        coverImageUrl: book.coverImageUrl || '',

        primaryColor: book?.styling?.primaryColor || '#2563eb',
        backgroundColor: book?.styling?.backgroundColor || '#f8fafc',
        headerColor: book?.styling?.headerColor || '#ffffff',
        headerTextColor: book?.styling?.headerTextColor || '#1e293b',
        accentColor: book?.styling?.accentColor || '#4f46e5',
        fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif',
        headerBackgroundColor: book?.styling?.headerBackgroundColor || '#ffffff',
        textColor: book?.styling?.textColor || '#1e293b',
        headerRadius: book?.styling?.headerRadius || '0px',
        logoInvert: book?.styling?.logoInvert ?? 0,
        cardRadius: book?.styling?.cardRadius || '12px',
        shadowLevel: book?.styling?.shadowLevel || 'medium',

        headerSettings: {
          showLogo: book.headerSettings?.showLogo ?? true,
          showTitle: book.headerSettings?.showTitle ?? true,
          showDescription: book.headerSettings?.showDescription ?? false,
          headerHeight: book.headerSettings?.headerHeight || 80,
          logoHeight: book.headerSettings?.logoHeight || 40,
          position: book.headerSettings?.position || 'sticky',
          layout: book.headerSettings?.layout || 'default'
        },

        accessLevel: book.accessLevel || 'public',
        accessPassword: book.accessPassword || '',
        requireAuth: book.requireAuth ?? false,
        authorizedUsers: initializeAuthorizedUsers(book.authorizedUsers),
        viewAccess: book.viewAccess || {
          enablePublicView: true,
          enableManagementView: true,
          allowViewToggle: true,
          defaultView: 'public',
          requireAuthForManagement: true
        },

        seoTitle: book.seoTitle || '',
        seoDescription: book.seoDescription || '',
        seoKeywords: book.seoKeywords || '',
        favicon: book.favicon || '',
        ogImage: book.ogImage || '',
        customDomain: book.customDomain || '',
        customPath: book.customPath || '',
        schoolmateSettings: {
          schoolName: '',
          schoolType: 'combined',
          motto: '',
          address: '',
          phone: '',
          email: '',
          enabledGrades: ['8', '9', '10', '11', '12'],
          termStructure: 'quarterly',
          currentTerm: 1,
          academicYear: new Date().getFullYear(),
          schoolDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          periodsPerDay: 8,
          periodDuration: 45,
          schoolStartTime: '07:30',
          schoolEndTime: '14:30',
          breakTimes: [],
          enabledFeatures: ['timetable', 'lessons', 'attendance', 'assessments'],
          ...book.schoolmateSettings
        }
      });
    }
  }, [book]);

  const initializeAuthorizedUsers = (authorizedUsers) => {
    if (!authorizedUsers) return {};
    if (!Array.isArray(authorizedUsers) && typeof authorizedUsers === 'object') {
      return authorizedUsers;
    }
    if (Array.isArray(authorizedUsers)) {
      return authorizedUsers.reduce((acc, email) => {
        acc[email] = {
          email,
          permissions: {
            management: true,
            voiceRecording: true,
            uploads: true
          },
          addedAt: new Date().toISOString()
        };
        return acc;
      }, {});
    }
    return {};
  };

  const handleInputChange = (path, value) => {
    setHasChanges(true);
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file || !bookId) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [fileType]: 'File size must be less than 5MB' }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [fileType]: 'uploading' }));
    setErrors(prev => ({ ...prev, [fileType]: null }));

    try {
      const path = `pb-books/${bookId}/assets/${fileType}`;
      const uploadResult = await firestoreService.uploadFile(file, path, bookId);
      const urlString = typeof uploadResult === 'string'
        ? uploadResult
        : uploadResult?.url || uploadResult;

      if (!urlString || typeof urlString !== 'string') {
        throw new Error('Invalid URL returned from upload');
      }

      const fieldMapping = {
        logo: 'logoUrl',
        logoUrl: 'logoUrl',
        coverImage: 'coverImageUrl',
        coverImageUrl: 'coverImageUrl',
        headerBackground: 'headerBackgroundUrl',
        favicon: 'favicon',
        ogImage: 'ogImage'
      };

      const fieldName = fieldMapping[fileType] || fileType;
      handleInputChange(fieldName, urlString);

      setUploadStates(prev => ({ ...prev, [fileType]: 'success' }));
      setTimeout(() => {
        setUploadStates(prev => ({ ...prev, [fileType]: null }));
      }, 2000);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStates(prev => ({ ...prev, [fileType]: 'error' }));
      setErrors(prev => ({ ...prev, [fileType]: error.message || 'Upload failed' }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedBook = {
        botName: formData.botName,
        description: formData.description,
        logoUrl: formData.logoUrl || '',
        coverImageUrl: formData.coverImageUrl || '',
        accessLevel: formData.accessLevel,
        accessPassword: formData.accessPassword || '',
        requireAuth: formData.requireAuth === true,
        authorizedUsers: formData.authorizedUsers || {},
        viewAccess: formData.viewAccess || {
          enablePublicView: true,
          enableManagementView: true,
          allowViewToggle: true,
          defaultView: 'public',
          requireAuthForManagement: true
        },
        styling: {
          primaryColor: formData.primaryColor || '#2563eb',
          backgroundColor: formData.backgroundColor || '#f8fafc',
          headerColor: formData.headerColor || '#ffffff',
          headerTextColor: formData.headerTextColor || '#1e293b',
          accentColor: formData.accentColor || '#4f46e5',
          fontFamily: formData.fontFamily || 'Inter, sans-serif',
          headerBackgroundColor: formData.headerBackgroundColor || '#ffffff',
          textColor: formData.textColor || '#1e293b',
          headerRadius: formData.headerRadius || '0px',
          logoInvert: formData.logoInvert ?? 0,
          cardRadius: formData.cardRadius || '12px',
          shadowLevel: formData.shadowLevel || 'medium'
        },
        headerSettings: formData.headerSettings || {},
        schoolmateSettings: formData.schoolmateSettings || {},
        seoTitle: formData.seoTitle || '',
        seoDescription: formData.seoDescription || '',
        seoKeywords: formData.seoKeywords || '',
        favicon: formData.favicon || '',
        ogImage: formData.ogImage || '',
        customDomain: formData.customDomain || '',
        customPath: formData.customPath || '',
        updatedAt: new Date()
      };

      await firestoreService.updateBook(bookId, updatedBook);
      onUpdate?.({ ...book, ...updatedBook });
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
    }
    setSaving(false);
  };

  const toggleFeature = (key) => {
    const current = formData.schoolmateSettings?.enabledFeatures || [];
    const newFeatures = current.includes(key) ? current.filter(f => f !== key) : [...current, key];
    handleInputChange('schoolmateSettings.enabledFeatures', newFeatures);
  };

  const toggleGrade = (grade) => {
    const current = formData.schoolmateSettings?.enabledGrades || [];
    const newGrades = current.includes(grade) ? current.filter(g => g !== grade) : [...current, grade];
    handleInputChange('schoolmateSettings.enabledGrades', newGrades);
  };

  const toggleDay = (day) => {
    const current = formData.schoolmateSettings?.schoolDays || [];
    const newDays = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
    handleInputChange('schoolmateSettings.schoolDays', newDays);
  };

  const renderTabContent = () => {
    const settings = formData.schoolmateSettings || {};

    switch (activeTab) {
      case 'basic':
        return <BasicInfoTab formData={formData} onInputChange={handleInputChange} onFileUpload={handleFileUpload} uploadStates={uploadStates} errors={errors} templateType="schoolmate" bookId={bookId} />;

      case 'school':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-4">School Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                  <input type="text" value={settings.schoolName || ''} onChange={(e) => handleInputChange('schoolmateSettings.schoolName', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="ABC High School" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Type</label>
                  <select value={settings.schoolType || 'combined'} onChange={(e) => handleInputChange('schoolmateSettings.schoolType', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    {SCHOOL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motto</label>
                  <input type="text" value={settings.motto || ''} onChange={(e) => handleInputChange('schoolmateSettings.motto', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Excellence in Education" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={settings.phone || ''} onChange={(e) => handleInputChange('schoolmateSettings.phone', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={settings.email || ''} onChange={(e) => handleInputChange('schoolmateSettings.email', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" value={settings.address || ''} onChange={(e) => handleInputChange('schoolmateSettings.address', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Academic Structure</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Term Structure</label>
                  <select value={settings.termStructure || 'quarterly'} onChange={(e) => handleInputChange('schoolmateSettings.termStructure', e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                    {TERM_STRUCTURES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Term</label>
                  <input type="number" min="1" max="4" value={settings.currentTerm || 1} onChange={(e) => handleInputChange('schoolmateSettings.currentTerm', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
                  <input type="number" value={settings.academicYear || new Date().getFullYear()} onChange={(e) => handleInputChange('schoolmateSettings.academicYear', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Enabled Grades</label>
                <div className="flex flex-wrap gap-2">
                  {GRADE_LEVELS.map(g => (
                    <button key={g} type="button" onClick={() => toggleGrade(g)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${settings.enabledGrades?.includes(g) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                      Gr {g}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-4">School Hours</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input type="time" value={settings.schoolStartTime || '07:30'} onChange={(e) => handleInputChange('schoolmateSettings.schoolStartTime', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input type="time" value={settings.schoolEndTime || '14:30'} onChange={(e) => handleInputChange('schoolmateSettings.schoolEndTime', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Periods/Day</label>
                  <input type="number" min="1" max="12" value={settings.periodsPerDay || 8} onChange={(e) => handleInputChange('schoolmateSettings.periodsPerDay', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period Duration</label>
                  <select value={settings.periodDuration || 45} onChange={(e) => handleInputChange('schoolmateSettings.periodDuration', parseInt(e.target.value))} className="w-full px-3 py-2 border rounded-lg">
                    <option value={30}>30 min</option>
                    <option value={35}>35 min</option>
                    <option value={40}>40 min</option>
                    <option value={45}>45 min</option>
                    <option value={50}>50 min</option>
                    <option value={60}>60 min</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School Days</label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map(d => (
                  <button key={d} type="button" onClick={() => toggleDay(d)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border capitalize transition-colors ${settings.schoolDays?.includes(d) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                    {d.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Enabled Modules</h3>
              <p className="text-sm text-gray-500 mb-4">Select which features to enable</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(FEATURES).map(([key, f]) => {
                  const Icon = f.icon;
                  const enabled = settings.enabledFeatures?.includes(key);
                  return (
                    <button key={key} type="button" onClick={() => toggleFeature(key)} className={`p-4 rounded-xl border-2 text-left transition-all ${enabled ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${enabled ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-medium">{f.label}</h4>
                          <p className="text-sm text-gray-500">{f.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'header':
        return <HeaderSettings formData={formData} onInputChange={handleInputChange} onImageUpload={(file, type) => handleFileUpload(file, `styling.${type}`)} uploadStates={uploadStates} />;

      case 'design':
        return <DesignBrandingSection formData={formData} onInputChange={handleInputChange} />;

      case 'privacy':
        return <PrivacySection formData={formData} onInputChange={handleInputChange} templateType="schoolmate" />;

      case 'seo':
        return <SEOSection formData={formData} onInputChange={handleInputChange} onImageUpload={handleFileUpload} uploadStates={uploadStates} errors={errors} bookId={bookId} />;

      default:
        return null;
    }
  };

  return (
    <SettingsModal isOpen={true} onClose={onClose} title="School Settings" subtitle="Configure your school management system" onSave={handleSave} saving={saving} hasChanges={hasChanges}>
      <div className="flex h-full">
        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>  
      <div className="flex-1 overflow-y-auto p-6">
          {renderTabContent()}
      </div>
    </SettingsModal>
  );
};

export default SchoolmateSettings;
